import { useEffect, useState, useRef, Fragment } from "react";
import Modal from "react-bootstrap/Modal";
import ReCAPTCHA from "react-google-recaptcha";
import InputMask from "react-input-mask";
import { useFormik } from "formik";
import Instructions from "./components/Instructions";
import { statesAndProvinces, donationRegions, months, getYears } from "./helpers/ui";
import { formatCurrency, postalCodeMask, zipCodeMask, phoneMask } from "./utils";
import { postDonation } from "./services/donationService";

function App() {
  const [showDonationModal, setShowDonationModal] = useState(false);
  const [showInformationModal, setShowInformationModal] = useState(false);
  const [showCvvModal, setShowCvvModal] = useState(false);
  const [newDonationAmount, setNewDonationAmount] = useState(25.0);
  const [activePage, setActivePage] = useState("form");
  const [pageTitle, setPageTitle] = useState("Christmas Daddies Online Donation Form");
  const [zipPostalMask, setZipPostalMask] = useState(postalCodeMask);
  const [telephoneMask, setTelephoneMask] = useState(phoneMask);
  const [validationErrors, setValidationErrors] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const isProcessingRef = useRef(false);
  const formLoadTimeRef = useRef(Date.now()); // for timing checks

  const [receipt, setReceipt] = useState({});

  const validYears = getYears();

  const validate = (values) => {
    const errors = {};

    // Honeypot strict block: if this has any value, treat as bot
    if (values.honeypot) {
      errors.honeypot = "Bot detected";
    }

    if (!values.firstName) {
      errors.firstName = "Required";
    }

    if (!values.lastName) {
      errors.lastName = "Required";
    }

    if (!values.address1) {
      errors.address1 = "Required";
    }

    if (!values.city) {
      errors.city = "Required";
    }

    if (!values.province) {
      errors.province = "Required";
    }

    if (!values.postalCode) {
      errors.postalCode = "Required";
    }

    if (!values.email) {
      errors.email = "Required";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)) {
      errors.email = "Invalid email address";
    }

    if (!values.telephone) {
      errors.telephone = "Required";
    }

    if (!values.doNotDisplayDonation === true) {
      if (!values.display1) {
        errors.display1 = "Required";
      }

      if (!values.display2) {
        errors.display2 = "Required";
      }
    }

    if (!values.region) {
      errors.region = "Required";
    }

    if (!values.creditCard) {
      errors.creditCard = "Required";
    } else {
      const cardNumber = (values.creditCard + "").replace(/\s+/g, "");
      const visaPattern = /^4[0-9]{12}(?:[0-9]{3})?$/;
      const mastercardPattern = /^5[1-5][0-9]{14}$/;
      const amexPattern = /^3[47][0-9]{13}$/;

      if (visaPattern.test(cardNumber)) {
        // Visa card is valid
      } else if (mastercardPattern.test(cardNumber)) {
        // Mastercard is valid
      } else if (amexPattern.test(cardNumber)) {
        // American Express card is valid
      } else {
        errors.creditCard = "Invalid credit card number";
      }
    }

    if (!values.cvv) {
      errors.cvv = "Required";
    } else if (new RegExp(/^[0-9]{3,4}$/).test(values.cvv) === false) {
      errors.cvv = "Invalid cvv";
    }

    if (!values.expiryMonth) {
      errors.expiryMonth = "Required";
    }

    if (!values.expiryYear) {
      errors.expiryYear = "Required";
    }

    if (values.expiryMonth && values.expiryYear) {
      const expiryDate = new Date(parseInt(values.expiryYear) + 2000, values.expiryMonth, 0);
      const today = new Date();

      if (expiryDate < today) {
        errors.expiryMonth = "Invalid expiry date";
        errors.expiryYear = "Invalid expiry date";
      }
    }

    if (!values.recaptcha) {
      errors.recaptcha = "Required";
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: {
      // Honeypot for bot detection
      honeypot: "",
      companyName: "",
      firstName: "",
      lastName: "",
      address1: "",
      address2: "",
      aptNo: "",
      poBox: "",
      city: "",
      province: "",
      postalCode: "",
      email: "",
      telephone: "",

      doNotDisplayDonation: false,
      display1: "",
      display2: "",
      region: "",
      donationAmount: 25.0,

      creditCard: "",
      cvv: "",
      expiryMonth: "",
      expiryYear: "",
      recaptcha: "",
    },
    validate,
    onSubmit: async (values) => {
      // Timing check
      const now = Date.now();
      const elapsedSeconds = (now - formLoadTimeRef.current) / 1000;

      // Clearly impossible for a human on this form
      if (elapsedSeconds < 1) {
        alert("We were unable to process your donation. Please take a moment to complete the form and try again.");
        return;
      }

      // Fast but still *possible*: add soft confirmation
      if (elapsedSeconds < 3) {
        const confirmFast = window.confirm("This donation was submitted very quickly. If you're a real donor, please confirm to continue.");
        if (!confirmFast) {
          return;
        }
      }

      if (isProcessingRef.current) {
        return;
      }

      isProcessingRef.current = true;
      setIsProcessing(true);

      try {
        const receipt = await postDonation(values);

        window.scrollTo(0, 0);

        if (receipt.status === "VALIDATION-ERRORS") {
          setValidationErrors(receipt.errors);
          return;
        }

        setValidationErrors(null);
        setReceipt(receipt);
        setActivePage("receipt");
        if (receipt.status === "APPROVED") {
          setPageTitle("Thank You!");
        } else {
          setPageTitle("Error Authorizing Payment");
        }
      } finally {
        isProcessingRef.current = false;
        setIsProcessing(false);
      }
    },
  });

  useEffect(() => {
    formik.validateForm();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (activePage === "form") {
      setPageTitle("Christmas Daddies Online Donation Form");
      // Reset timing when we’re on the form
      formLoadTimeRef.current = Date.now();
    } else {
      setPageTitle("Thank You!");
    }
  }, [activePage]);

  useEffect(() => {
    if ((receipt.status || "") === "DECLINED") {
      setPageTitle("Error Authorizing Payment");
    }
    if ((receipt.status || "") === "APPROVED") {
      setPageTitle("Thank You!");
    }
    if ((receipt.status || "") === "ERROR") {
      setPageTitle("Error Authorizing Payment");
    }
  }, [receipt]);

  useEffect(() => {
    if (formik.values.doNotDisplayDonation === true) {
      formik.setFieldValue("display1", "");
      formik.setFieldValue("display2", "");
    }
  }, [formik.values.doNotDisplayDonation]);

  useEffect(() => {
    if (["AB", "BC", "MB", "NB", "NL", "NT", "NS", "NU", "ON", "PE", "QC", "SK", "YT"].some((i) => i === formik.values.province)) {
      setZipPostalMask(postalCodeMask);
      setTelephoneMask(phoneMask);
      return;
    }

    if (formik.values.province === "ZZ") {
      setZipPostalMask(null);
      setTelephoneMask(null);
      return;
    }

    setZipPostalMask(zipCodeMask);
    setTelephoneMask(phoneMask);
  }, [formik.values.province]);

  const handleShowDonationModal = () => {
    setShowDonationModal(true);
  };

  const handleCloseDonationModal = () => {
    setShowDonationModal(false);
  };

  const handleNewDonationAmountChange = (e) => {
    setNewDonationAmount(e.target.value);
  };

  const handleUpdateDonationAmount = () => {
    formik.setFieldValue("donationAmount", parseFloat(newDonationAmount));
    setShowDonationModal(false);
  };

  const handleRecaptchaChange = (value) => {
    formik.setFieldValue("recaptcha", value || "");
  };

  const handleTryAgainClick = () => {
    isProcessingRef.current = false;
    setIsProcessing(false);
    formik.setFieldValue("recaptcha", "");
    setReceipt({});
    setActivePage("form");
  };

  return (
    <>
      <header className="entry-content-header">
        <h1 className="post-title entry-title" itemProp="headline">
          {pageTitle}
        </h1>
      </header>
      <div className="donations-app bootstrap-styles">
        {/* VALIDATION ERRORS */}
        {validationErrors && (
          <div className="alert alert-danger" role="alert">
            <div dangerouslySetInnerHTML={{ __html: validationErrors }}></div>
          </div>
        )}

        {/* FORM */}
        {activePage === "form" && (
          <>
            <div className="row">
              <div className="col-xs-8">
                <form onSubmit={formik.handleSubmit}>
                  {/* Honeypot field - hidden from real users */}
                  <input
                    type="text"
                    name="honeypot"
                    value={formik.values.honeypot}
                    onChange={formik.handleChange}
                    autoComplete="off"
                    tabIndex="-1"
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      left: "-9999px",
                      top: "auto",
                      width: "1px",
                      height: "1px",
                      overflow: "hidden",
                    }}
                  />

                  {/* NAME AND ADDRESS */}
                  <div className="well">
                    <h3>Name and Address</h3>

                    <div className="row">
                      <div className="col-xs-6">
                        <div className="form-group">
                          <label htmlFor="companyName">Company Name:</label>
                          <input type="text" className="form-control upper" tabIndex="1" id="companyName" name="companyName" onChange={formik.handleChange} value={formik.values.companyName} />
                          <p style={{ fontSize: "smaller", marginLeft: "3px", marginTop: "-19px" }}>Note that company is not a required field.</p>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-xs-6">
                        <div className="form-group">
                          <label htmlFor="firstName">* First Name:</label>
                          <input type="text" tabIndex="2" id="firstName" name="firstName" required={true} onChange={formik.handleChange} value={formik.values.firstName} className={`form-control upper ${formik.errors.firstName ? "required" : "valid"}`} />
                          <p style={{ fontSize: "smaller", marginLeft: "3px", marginTop: "-19px" }}>A charitable tax receipt can only be issued to one individual. The two line "TV Display" can be edited below.</p>
                        </div>
                      </div>
                      <div className="col-xs-6">
                        <div className="form-group">
                          <label htmlFor="lastName">* Last Name:</label>
                          <input type="text" tabIndex="3" id="lastName" name="lastName" required={true} onChange={formik.handleChange} value={formik.values.lastName} className={`form-control upper ${formik.errors.lastName ? "required" : "valid"}`} />
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-xs-6">
                        <div className="form-group">
                          <label htmlFor="address1">* Address 1:</label>
                          <input type="text" tabIndex="4" id="address1" name="address1" required={true} onChange={formik.handleChange} value={formik.values.address1} className={`form-control upper ${formik.errors.address1 ? "required" : "valid"}`} />
                        </div>
                      </div>
                      <div className="col-xs-6">
                        <div className="form-group">
                          <label htmlFor="address2">Address 2:</label>
                          <input type="text" className="form-control upper" tabIndex="5" id="address2" name="address2" onChange={formik.handleChange} value={formik.values.address2} />
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-xs-6">
                        <div className="form-group">
                          <label htmlFor="AptNo">Apt #:</label>
                          <input type="text" className="form-control upper" tabIndex="6" id="aptNo" name="aptNo" onChange={formik.handleChange} value={formik.values.aptNo} />
                        </div>
                      </div>
                      <div className="col-xs-6">
                        <div className="form-group">
                          <label htmlFor="POBox">PO Box:</label>
                          <input type="text" className="form-control upper" tabIndex="7" id="poBox" name="poBox" onChange={formik.handleChange} value={formik.values.poBox} />
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-xs-6">
                        <div className="form-group">
                          <label htmlFor="city">* City:</label>
                          <input type="text" tabIndex="8" id="city" name="city" onChange={formik.handleChange} value={formik.values.city} className={`form-control upper ${formik.errors.city ? "required" : "valid"}`} />
                        </div>
                      </div>
                      <div className="col-xs-6">
                        <div className="form-group">
                          <label htmlFor="province">* Province/State:</label>
                          <select id="province" tabIndex="9" name="province" onChange={formik.handleChange} value={formik.values.province} className={`form-control ${formik.errors.province ? "required" : "valid"}`}>
                            <option value="">Please Select</option>
                            {statesAndProvinces.map((group) => (
                              <Fragment key={group.text}>
                                <optgroup label={group.text} />
                                {group.items.map((item) => (
                                  <option value={item.value} key={item.id}>
                                    {item.text}
                                  </option>
                                ))}
                              </Fragment>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-xs-6">
                        <div className="form-group">
                          <label htmlFor="postalCode">* Postal/Zip Code:</label>
                          <InputMask type="text" tabIndex="10" id="postalCode" name="postalCode" onChange={formik.handleChange} value={formik.values.postalCode} className={`form-control upper ${formik.errors.postalCode ? "required" : "valid"}`} mask={zipPostalMask} />
                        </div>
                      </div>
                      <div className="col-xs-6">
                        <div className="form-group">
                          <label htmlFor="email">* Email:</label>
                          <input type="email" tabIndex="11" id="email" name="email" onChange={formik.handleChange} value={formik.values.email} className={`form-control upper ${formik.errors.email ? "required" : "valid"}`} />
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-xs-6">
                        <div className="form-group">
                          <label htmlFor="telephone">* Telephone:</label>
                          <InputMask type="text" tabIndex="12" id="telephone" name="telephone" onChange={formik.handleChange} value={formik.values.telephone} className={`form-control upper ${formik.errors.telephone ? "required" : "valid"}`} mask={telephoneMask} />
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* /NAME AND ADDRESS */}

                  {/* DONATION INFORMATION */}
                  <div className="well">
                    <h3>Donation Details</h3>
                    <div className="row">
                      <div className="col-xs-6">
                        Donation Amount: <span style={{ marginRight: "1rem" }}>{formatCurrency(formik.values.donationAmount)}</span>
                        <button type="button" className="btn btn-xs btn-success" tabIndex="13" onClick={() => handleShowDonationModal()}>
                          Change
                        </button>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-xs-12">
                        <div className="checkbox">
                          <label>
                            <input type="checkbox" style={{ height: "21px", width: "21px" }} tabIndex="14" id="doNotDisplayDonation" name="doNotDisplayDonation" onChange={formik.handleChange} value={formik.values.doNotDisplayDonation} checked={formik.values.doNotDisplayDonation ? true : false} />
                            <span style={{ marginLeft: "1em" }}>I do not want my display names to be shown on air.</span>
                          </label>
                        </div>
                      </div>
                    </div>
                    {!formik.values.doNotDisplayDonation && (
                      <div className="row" id="on-air-display-details">
                        <div className="col-xs-5">
                          <div className="form-group">
                            <label htmlFor="Display1">* TV Display Line 1:</label>
                            <div className="input-group">
                              <input type="text" className={`form-control upper ${formik.errors.display1 ? "required" : "valid"}`} tabIndex="15" id="display1" name="display1" maxLength={32} onChange={formik.handleChange} value={formik.values.display1} style={{ marginBottom: 0 }} />
                              <div style={{ fontSize: "smaller", marginLeft: "0.25rem" }}>Required</div>
                            </div>
                          </div>
                        </div>
                        <div className="col-xs-2">
                          <div className="input-group-btn" style={{ textAlign: "center", paddingTop: "33px" }}>
                            <a
                              href="#"
                              tabIndex="16"
                              onClick={(e) => {
                                e.preventDefault();
                                setShowInformationModal(true);
                              }}
                            >
                              <span style={{ fontSize: "26px" }} className="glyphicon glyphicon-question-sign" aria-hidden="true"></span>
                            </a>
                          </div>
                        </div>
                        <div className="col-xs-5">
                          <div className="form-group">
                            <label htmlFor="Display2">* TV Display Line 2:</label>
                            <input type="text" className={`form-control upper ${formik.errors.display2 ? "required" : "valid"}`} id="display2" tabIndex="17" name="display2" maxLength={32} onChange={formik.handleChange} value={formik.values.display2} style={{ marginBottom: 0 }} />
                            <div style={{ fontSize: "smaller", marginLeft: "0.25rem" }}>Required</div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="row">
                      <div className="col-xs-12">
                        <div className="form-group">
                          <label htmlFor="region">* What area do you request your donation to go?:</label>
                          <select id="region" name="region" tabIndex="18" onChange={formik.handleChange} value={formik.values.region} className={`form-control ${formik.errors.region ? "required" : "valid"}`}>
                            <option value="">Please Select</option>
                            {donationRegions.map((item) => (
                              <option value={item.value} key={item.value}>
                                {item.text}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* /DONATION INFORMATION */}

                  {/* PAYMENT INFORMATION */}
                  <div className="well">
                    <h3>Payment Details</h3>
                    <div className="row">
                      <div className="col-xs-4">
                        <h3>
                          <span>{formatCurrency(formik.values.donationAmount)}</span>
                        </h3>
                      </div>
                      <div className="col-xs-8">
                        <div className="pull-right credit-card-logos">
                          <img src="assets/img/visa-logo.png" alt="visa logo" />
                          <img src="assets/img/mastercard-logo.png" alt="master card logo" />
                          <img src="assets/img/american-express-logo.png" alt="american express logo" />
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-xs-12 col-md-6">
                        <div className="form-group">
                          <label htmlFor="creditCard">* Credit Card Number</label>
                          <input type="number" tabIndex="19" id="creditCard" name="creditCard" onChange={formik.handleChange} value={formik.values.creditCard} className={`form-control upper ${formik.errors.creditCard ? "required" : "valid"}`} />
                        </div>
                      </div>
                      <div className="col-xs-12 col-md-6">
                        <div className="form-group">
                          <label htmlFor="CVV">
                            * CVV/CVC (
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                setShowCvvModal(true);
                              }}
                            >
                              What is this?
                            </a>
                            )
                          </label>
                          <input type="text" tabIndex="20" id="cvv" name="cvv" onChange={formik.handleChange} value={formik.values.cvv} className={`form-control upper ${formik.errors.cvv ? "required" : "valid"}`} />
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-xs-6">
                        <div className="form-group">
                          <label htmlFor="expiryMonth">* Expiry Month:</label>
                          <select tabIndex="21" id="expiryMonth" name="expiryMonth" className={`form-control ${formik.errors.expiryMonth ? "required" : "valid"}`} onChange={formik.handleChange} value={formik.values.expiryMonth}>
                            <option value="">Please Select</option>
                            {months.map((item) => (
                              <option value={item.value} key={item.value}>
                                {item.text}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="col-xs-6">
                        <div className="form-group">
                          <label htmlFor="expiryYear">* Expiry Year:</label>
                          <select tabIndex="22" id="expiryYear" name="expiryYear" onChange={formik.handleChange} value={formik.values.expiryYear} className={`form-control ${formik.errors.expiryYear ? "required" : "valid"}`}>
                            <option value="">Please Select</option>
                            {validYears.map((item) => (
                              <option value={item.value} key={item.value}>
                                {item.text}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-xs-12">
                        Contributions of $20 will be issued charitable tax receipts by The Salvation Army. Donors who provide a valid email address will have their charitable tax receipt emailed, otherwise your charitable tax receipt will be mailed. Receipts are issued in January and February.
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-xs-6">
                        <ReCAPTCHA sitekey="6LfZOAsUAAAAAJSCcuktBBt6pDCtfEb5HW4a0bQL" onChange={handleRecaptchaChange} />
                      </div>
                      <div className="col-xs-6">
                        <button type="submit" style={{ marginTop: "20px" }} tabIndex="23" className={`btn center-block ${formik.isValid && !isProcessing ? "btn-success" : "btn-default"}`} disabled={!formik.isValid || isProcessing}>
                          {isProcessing ? "Processing..." : "Click to complete your donation"}
                        </button>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-xs-12">The Christmas Daddies Telethon is a volunteer effort by the staff of CTV. Since 1964, funds raised have been putting smiles on the faces of less fortunate children at Christmas time.</div>
                    </div>
                  </div>
                  {/* /PAYMENT INFORMATION */}
                </form>
              </div>

              <div className="col-xs-4">
                <Instructions />
              </div>
            </div>
            {/* MODALS */}

            {/* DONATION MODAL */}
            <Modal show={showDonationModal} onHide={handleCloseDonationModal} backdrop="static" style={{ marginTop: "0" }} size="sm" aria-labelledby="contained-modal-title-vcenter" centered>
              <Modal.Header closeButton>
                <Modal.Title>Donation Amount</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <input type="number" step="0.01" min="1" id="donationAmount" name="donationAmount" className="form-control" aria-label="Amount (to the nearest dollar)" style={{ textAlign: "right" }} onChange={handleNewDonationAmountChange} value={newDonationAmount} />
              </Modal.Body>
              <Modal.Footer>
                <button type="button" className="btn btn-success" data-dismiss="modal" onClick={handleUpdateDonationAmount}>
                  Update
                </button>
              </Modal.Footer>
            </Modal>
            {/* /DONATION MODAL */}

            {/* INFORMATION MODAL */}
            <Modal
              show={showInformationModal}
              onHide={() => {
                setShowInformationModal(false);
              }}
              backdrop="static"
              style={{ marginTop: "0" }}
              size="lg"
              aria-labelledby="contained-modal-title-vcenter"
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>Important to know!</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p>Each line is limited to 32 characters. This includes spaces and punctuation.</p>
                <p>If your donation is made in memory of someone please use the abbreviation IMO</p>
                <p>
                  <span style={{ fontWeight: "bold" }}>Example:</span> <br /> TV Display Line 1: <span style={{ fontFamily: "monospace", fontSize: "20px", color: "blue" }}>IMO Grampie Pat</span> <br /> TV Display Line 2:{" "}
                  <span style={{ fontFamily: "monospace", fontSize: "20px", color: "blue" }}>From his 18 Grandchildren</span>
                </p>
                <p>
                  <span style={{ fontWeight: "bold" }}>To submit a company name:</span> <br /> TV Display Name 1: <span style={{ fontFamily: "monospace", fontSize: "20px", color: "blue" }}>Staff and Customers of</span> <br /> TV Display Name 2:{" "}
                  <span style={{ fontFamily: "monospace", fontSize: "20px", color: "blue" }}>The Best Pizza House, HFX</span>
                </p>
                <p>
                  Remember, each line is limited to 32 characters. This includes spaces and punctuation. Entries are reviewed for content before broadcast. Inappropriate content will not be permitted. Lastly, TV display lines are only available during the live television broadcast. The Christmas
                  Daddies Telethon is held on the first Saturday in December each year.
                </p>
              </Modal.Body>
              <Modal.Footer>
                <button type="button" className="btn btn-success" onClick={() => setShowInformationModal(false)}>
                  Close
                </button>
              </Modal.Footer>
            </Modal>
            {/* /INFORMATION MODAL */}

            {/* CVV MODAL */}
            <Modal show={showCvvModal} onHide={() => setShowCvvModal(false)} backdrop="static" style={{ marginTop: "0" }} aria-labelledby="contained-modal-title-vcenter" centered>
              <Modal.Header closeButton>
                <Modal.Title>What is CVV?</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p>CVV and CVC data helps identify you as the legitimate card user, helping to prevent credit card fraud. The image below can help you locate the information on your card.</p>
                <img src="./images/cvv.gif" alt="photo of cvv on the back of credit cards" />
              </Modal.Body>
              <Modal.Footer>
                <button type="button" className="btn btn-success" data-dismiss="modal" onClick={() => setShowCvvModal(false)}>
                  Ok
                </button>
              </Modal.Footer>
            </Modal>
            {/* /CVV MODAL */}

            {/* /MODALS */}
          </>
        )}
        {/* /FORM */}

        {/* RECEIPT */}
        {activePage === "receipt" && (
          <div>
            <table className="receipt" style={{ padding: "0", width: "66%" }}>
              <tbody>
                <tr>
                  {receipt.status === "APPROVED" ? (
                    <td
                      colSpan="2"
                      style={{
                        textAlign: "center",
                        fontSize: "larger",
                        color: "#008000",
                        fontWeight: "bold",
                      }}
                    >
                      <br />
                      {receipt.message}
                      <br />
                      &nbsp;
                    </td>
                  ) : receipt.status === "DECLINED" ? (
                    <td
                      colSpan="2"
                      style={{
                        textAlign: "center",
                        fontSize: "larger",
                        color: "red",
                        fontWeight: "bold",
                      }}
                    >
                      <br />
                      Payment Authorization Failed
                      <br />
                      &nbsp;
                    </td>
                  ) : (
                    <td
                      colSpan="2"
                      style={{
                        textAlign: "center",
                        fontSize: "larger",
                        color: "red",
                        fontWeight: "bold",
                      }}
                    >
                      <br />
                      We were unable to confirm the result of your donation.
                      <br />
                      &nbsp;
                    </td>
                  )}
                </tr>

                <tr>
                  <td className="receiptLabel">Authorization Status:</td>
                  <td
                    className="receiptTitle"
                    style={{
                      fontWeight: "bold",
                      color: receipt.status === "APPROVED" ? "green" : "red",
                    }}
                  >
                    {receipt.status === "APPROVED" ? "APPROVED" : receipt.status === "DECLINED" ? "FAILED" : "ERROR"}
                  </td>
                </tr>
                {receipt.status === "APPROVED" && (
                  <>
                    <tr>
                      <td className="receiptLabel">Transaction #:</td>
                      <td className="receiptField">{receipt.transactionNumber}</td>
                    </tr>
                    <tr>
                      <td className="receiptLabel">Auth. Code:</td>
                      <td className="receiptField">{receipt.authorizationCode}</td>
                    </tr>
                  </>
                )}
                {receipt.status !== "APPROVED" && (
                  <tr>
                    <td className="receiptLabel">Reason:</td>
                    <td className="receiptField" style={{ color: "red", fontWeight: "bold" }}>
                      {receipt.message}
                    </td>
                  </tr>
                )}
                <tr>
                  <td className="receiptLabel">Transaction Date:</td>
                  <td className="receiptField">{receipt.transactionDate}</td>
                </tr>
                <tr>
                  <td className="receiptLabel">Transaction Amount:</td>
                  <td className="receiptField">{formatCurrency(receipt.transactionAmount)}</td>
                </tr>
                <tr>
                  <td className="receiptLabel">Credit Card:</td>
                  <td className="receiptField">{receipt.creditCard}</td>
                </tr>
                <tr>
                  <td className="receiptLabel">Cardholder:</td>
                  <td className="receiptField">{receipt.cardHolderName}</td>
                </tr>
                <tr>
                  <td className="receiptLabel">Company Name:</td>
                  <td className="receiptField">{receipt.companyName}</td>
                </tr>
                {receipt.displayOnTv && (
                  <>
                    <tr>
                      <td className="receiptLabel">TV Display Line 1:</td>
                      <td className="receiptField">{receipt.display1}</td>
                    </tr>
                    <tr>
                      <td className="receiptLabel">TV Display Line 2:</td>
                      <td className="receiptField">{receipt.display2}</td>
                    </tr>
                  </>
                )}
                {!receipt.displayOnTv && (
                  <tr>
                    <td className="receiptLabel">Display Donation:</td>
                    <td className="receiptField">You have opted out of having your donation displayed on air.</td>
                  </tr>
                )}
              </tbody>
            </table>

            {receipt.status !== "APPROVED" && (
              <div>
                <button onClick={handleTryAgainClick} className="try-again">
                  Try Again
                </button>
              </div>
            )}
          </div>
        )}
        {/* /RECEIPT */}
      </div>
    </>
  );
}

export default App;
